import { looksLikeHtmlDocument, looksLikeHtmlFragment } from './htmlDocument'
import type {
  ChecklistItem,
  EmailOutputValue,
  KeyValueItem,
  OutputKind,
  OutputTypeHint,
  RenderableOutput,
  TableOutputValue,
  WorkflowOutputFile,
} from './outputTypes'

const FILE_MODEL_IDENTITY = '__dify__file__'
const ZOCKS_OUTPUT_TYPE = '__zocks_output_type__'

interface RawFile {
  url: string
  related_id?: unknown
  filename?: unknown
  mime_type?: unknown
  extension?: unknown
  size?: unknown
  dify_model_identity?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isRawFile = (value: unknown): value is RawFile => {
  if (!isRecord(value)) {
    return false
  }
  if (typeof value.url !== 'string' || value.url.length === 0) {
    return false
  }
  return value.dify_model_identity === FILE_MODEL_IDENTITY || typeof value.filename === 'string' || typeof value.mime_type === 'string'
}

const asString = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)

const toFile = (raw: RawFile): WorkflowOutputFile => ({
  url: raw.url,
  relatedId: asString(raw.related_id),
  filename: asString(raw.filename),
  mimeType: asString(raw.mime_type),
  extension: asString(raw.extension),
  size: typeof raw.size === 'number' && raw.size >= 0 ? raw.size : null,
})

const cellToString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value)
}

const toRecipients = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map((entry): string | null => {
      if (typeof entry === 'string') {
        return entry
      }
      if (isRecord(entry)) {
        const email = asString(entry.email)
        const name = asString(entry.name)
        if (email && name) {
          return `${name} <${email}>`
        }
        return email ?? name
      }
      return null
    })
    .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}

const toEmail = (value: Record<string, unknown>): EmailOutputValue => ({
  subject: asString(value.subject),
  htmlContent: asString(value.html_content),
  textContent: asString(value.text_content),
  recipients: toRecipients(value.recipients),
  cc: toRecipients(value.cc),
  bcc: toRecipients(value.bcc),
})

const toTable = (value: Record<string, unknown>): TableOutputValue => {
  const rawRows = Array.isArray(value.rows) ? value.rows : []
  let columns = Array.isArray(value.columns) ? value.columns.filter((column): column is string => typeof column === 'string') : []
  if (columns.length === 0) {
    const firstObject = rawRows.find(isRecord)
    if (firstObject) {
      columns = Object.keys(firstObject)
    }
  }
  const rows = rawRows.map((row): string[] => {
    if (Array.isArray(row)) {
      return row.map(cellToString)
    }
    if (isRecord(row)) {
      return columns.map((column) => cellToString(row[column]))
    }
    return [cellToString(row)]
  })
  return { columns, rows }
}

const toKeyValueItems = (value: Record<string, unknown>): KeyValueItem[] => {
  const items = Array.isArray(value.items) ? value.items : []
  return items
    .map((item): KeyValueItem | null => {
      if (!isRecord(item)) {
        return null
      }
      const label = asString(item.label)
      if (!label) {
        return null
      }
      return { label, value: cellToString(item.value) }
    })
    .filter((item): item is KeyValueItem => item !== null)
}

const toChecklistItems = (value: Record<string, unknown>): ChecklistItem[] => {
  const items = Array.isArray(value.items) ? value.items : []
  return items
    .map((item): ChecklistItem | null => {
      if (typeof item === 'string') {
        return item.length > 0 ? { text: item, checked: null } : null
      }
      if (isRecord(item)) {
        const text = asString(item.text)
        if (!text) {
          return null
        }
        return { text, checked: typeof item.checked === 'boolean' ? item.checked : null }
      }
      return null
    })
    .filter((item): item is ChecklistItem => item !== null)
}

const classifyTyped = (value: unknown): RenderableOutput | null => {
  if (!isRecord(value)) {
    return null
  }
  const type = value[ZOCKS_OUTPUT_TYPE]
  if (typeof type !== 'string') {
    return null
  }
  switch (type) {
    case 'email':
      return { kind: 'email', value: toEmail(value) }
    case 'html': {
      const content = asString(value.content)
      return content !== null ? { kind: 'html', value: content } : null
    }
    case 'markdown': {
      const content = asString(value.content)
      return content !== null ? { kind: 'markdown', value: content } : null
    }
    case 'file':
      return isRawFile(value) ? { kind: 'file', value: toFile(value) } : null
    case 'table':
      return { kind: 'table', value: toTable(value) }
    case 'keyValue':
      return { kind: 'keyValue', value: toKeyValueItems(value) }
    case 'checklist':
      return { kind: 'checklist', value: toChecklistItems(value) }
    default:
      return null
  }
}

const classifyByValue = (value: unknown): RenderableOutput => {
  if (value === null || value === undefined || value === '') {
    return { kind: 'empty' }
  }
  if (isRawFile(value)) {
    return { kind: 'file', value: toFile(value) }
  }
  if (Array.isArray(value) && value.length > 0 && value.every(isRawFile)) {
    return { kind: 'file-list', value: value.map(toFile) }
  }
  if (typeof value === 'string') {
    return looksLikeHtmlDocument(value) || looksLikeHtmlFragment(value) ? { kind: 'html', value } : { kind: 'text', value }
  }
  if (typeof value === 'number') {
    return { kind: 'number', value }
  }
  if (typeof value === 'boolean') {
    return { kind: 'boolean', value }
  }
  return { kind: 'json', value }
}

const KIND_BY_VALUE_TYPE: Record<string, OutputKind> = {
  file: 'file',
  'array[file]': 'file-list',
  string: 'text',
  number: 'number',
  boolean: 'boolean',
  html: 'html',
}

export const classifyOutput = (value: unknown, hint?: OutputTypeHint): RenderableOutput => {
  const typed = classifyTyped(value)
  if (typed) {
    return typed
  }
  const hintedKind = hint?.valueType ? KIND_BY_VALUE_TYPE[hint.valueType] : undefined
  if (hintedKind === 'file-list' && Array.isArray(value)) {
    return { kind: 'file-list', value: value.filter(isRawFile).map(toFile) }
  }
  if (hintedKind === 'file' && isRawFile(value)) {
    return { kind: 'file', value: toFile(value) }
  }
  if (hintedKind === 'html' && typeof value === 'string') {
    return { kind: 'html', value }
  }
  return classifyByValue(value)
}
